import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check, Waves, X } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";

function StatusCard({ children }) {
  return (
    <main className="grid min-h-screen place-items-center bg-abyss px-6 pt-[72px]" data-testid="payment-result-page">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className="glass-card max-w-md rounded-3xl p-10 text-center"
      >
        {children}
      </motion.div>
    </main>
  );
}

export function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [state, setState] = useState("waiting");
  const tries = useRef(0);
  const { tr } = useLang();

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let stop = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState("paid");
          return;
        }
      } catch {
        if (stop) return;
      }
      tries.current += 1;
      if (tries.current > 16) {
        setState("error");
        return;
      }
      if (!stop) setTimeout(poll, 2500);
    };
    poll();
    return () => {
      stop = true;
    };
  }, [sessionId]);

  return (
    <StatusCard>
      {state === "paid" ? (
        <>
          <span className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-aqua text-abyss" data-testid="payment-success-icon">
            <Check size={26} />
          </span>
          <h1 className="mt-6 font-serif text-3xl font-light text-foam">{tr("Votre place est réservée.")}</h1>
          <p className="mt-3 text-sm leading-relaxed text-foam/60" data-testid="payment-success-text">
            {tr("Le paiement du module est confirmé. Retrouvez votre session et votre progression dans votre espace praticien·ne.")}
          </p>
          <Link
            to="/tableau-de-bord"
            data-testid="payment-cta-dashboard"
            className="group mt-8 inline-flex items-center gap-2 rounded-full bg-aqua px-7 py-3.5 text-sm font-semibold text-abyss transition-all duration-300 hover:bg-foam"
          >
            {tr("Ouvrir mon espace")}
            <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </>
      ) : state === "waiting" ? (
        <>
          <span className="mx-auto grid h-16 w-16 place-items-center">
            <span className="h-12 w-12 rounded-full border-2 border-aqua/30 border-t-aqua animate-spin" />
          </span>
          <h1 className="mt-6 font-serif text-3xl font-light text-foam">{tr("Confirmation en cours…")}</h1>
          <p className="mt-3 text-sm text-foam/60" data-testid="payment-waiting-text">
            {tr("Nous validons votre paiement avec Stripe. Cela ne prend qu'un instant.")}
          </p>
        </>
      ) : (
        <>
          <Waves size={30} className="mx-auto text-aqua" />
          <h1 className="mt-6 font-serif text-3xl font-light text-foam">{tr("Paiement interrompu")}</h1>
          <p className="mt-3 text-sm text-foam/60">
            {tr("Aucun montant n'a été débité. Votre place vous attend — vous pouvez reprendre la réservation à tout moment.")}
          </p>
          <Link to="/tableau-de-bord" className="mt-8 inline-block rounded-full bg-aqua px-7 py-3.5 text-sm font-semibold text-abyss" data-testid="payment-error-cta">
            {tr("Ouvrir mon espace")}
          </Link>
        </>
      )}
    </StatusCard>
  );
}

export function PaymentCancel() {
  const { tr } = useLang();
  return (
    <StatusCard>
      <span className="mx-auto grid h-16 w-16 place-items-center rounded-full border border-aqua/40 text-aqua" data-testid="payment-cancel-icon">
        <X size={24} />
      </span>
      <h1 className="mt-6 font-serif text-3xl font-light text-foam">{tr("Paiement interrompu")}</h1>
      <p className="mt-3 text-sm leading-relaxed text-foam/60" data-testid="payment-cancel-text">
        {tr("Aucun montant n'a été débité. Votre place vous attend — vous pouvez reprendre la réservation à tout moment.")}
      </p>
      <Link
        to="/formations"
        data-testid="payment-retry-cta"
        className="group mt-8 inline-flex items-center gap-2 rounded-full bg-aqua px-7 py-3.5 text-sm font-semibold text-abyss transition-all duration-300 hover:bg-foam"
      >
        {tr("Revoir les formations")}
        <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
      </Link>
    </StatusCard>
  );
}
